import React, { useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { InlineSpinner } from '@/components/ui/inline-spinner';
import type { VoterTagDto } from '@signalvote/shared';
import {
  useTags,
  useCreateTag,
  useUpdateTag,
  useDeleteTag,
} from '../../../hooks/queries';

interface TagForm {
  tagName: string;
  description: string;
  color: string;
}

const TagsTab: React.FC = () => {
  // TanStack Query hooks
  const { data: tags = [], isLoading, error } = useTags();
  const createTag = useCreateTag();
  const updateTag = useUpdateTag();
  const deleteTag = useDeleteTag();

  // Local UI state
  const [tagDialog, setTagDialog] = useState(false);
  const [editingTag, setEditingTag] = useState<VoterTagDto | null>(null);
  const [tagForm, setTagForm] = useState<TagForm>({
    tagName: '',
    description: '',
    color: '#2196F3',
  });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const openCreateTagDialog = () => {
    setEditingTag(null);
    setTagForm({ tagName: '', description: '', color: '#2196F3' });
    setTagDialog(true);
  };

  const openEditTagDialog = (tag: VoterTagDto) => {
    setEditingTag(tag);
    setTagForm({
      tagName: tag.tagName,
      description: tag.description || '',
      color: tag.color || '#2196F3',
    });
    setTagDialog(true);
  };

  const handleSaveTag = async () => {
    if (!tagForm.tagName.trim()) {
      setErrorMessage('Tag name is required');
      return;
    }

    try {
      if (editingTag) {
        await updateTag.mutateAsync({
          id: editingTag.id,
          updates: {
            tagName: tagForm.tagName.trim(),
            description: tagForm.description.trim() || null,
            color: tagForm.color,
          },
        });
        setSuccessMessage('Tag updated successfully');
      } else {
        await createTag.mutateAsync({
          tagName: tagForm.tagName.trim(),
          description: tagForm.description.trim() || null,
          color: tagForm.color,
        });
        setSuccessMessage('Tag created successfully');
      }
      setTagDialog(false);
      setEditingTag(null);
      setTagForm({ tagName: '', description: '', color: '#2196F3' });
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Error saving tag');
    }
  };

  const handleDeleteTag = async (tag: VoterTagDto) => {
    if (!window.confirm(`Are you sure you want to delete the tag "${tag.tagName}"? This will remove the tag from all ${tag.voterCount} associated voters.`)) {
      return;
    }

    try {
      await deleteTag.mutateAsync(tag.id);
      setSuccessMessage('Tag deleted successfully');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Error deleting tag');
    }
  };

  const isSaving = createTag.isPending || updateTag.isPending;

  // Clear messages after 3 seconds
  React.useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
        setErrorMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  if (error) {
    return (
      <Alert severity="error" className="mb-6">
        {error instanceof Error ? error.message : 'Failed to load tags'}
      </Alert>
    );
  }

  return (
    <>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-2xl font-semibold">Tag Management</h2>
        <Button onClick={openCreateTagDialog}>
          <Plus />
          Create Tag
        </Button>
      </div>

      <p className="mb-4 text-sm text-muted-foreground">
        Create and manage tags to organize voters for targeted campaigns.
      </p>

      {successMessage && (
        <Alert severity="success" className="mb-4">
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" className="mb-4">
          {errorMessage}
        </Alert>
      )}

      {isLoading ? (
        <div className="flex justify-center py-8">
          <InlineSpinner />
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tag Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Color</TableHead>
                <TableHead className="text-right">Voters</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tags.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center">
                    <p className="py-8 text-sm text-muted-foreground">
                      No tags created yet. Create your first tag to get started.
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                tags.map((tag) => (
                  <TableRow key={tag.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div
                          className="h-3 w-3 rounded-full"
                          style={{ backgroundColor: tag.color || '#2196F3' }}
                        />
                        <span className="text-sm font-medium">
                          {tag.tagName}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {tag.description || '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge
                        className="font-mono text-[0.7rem] text-white"
                        style={{ backgroundColor: tag.color || '#2196F3' }}
                      >
                        {tag.color || '#2196F3'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline">{tag.voterCount}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {new Date(tag.createdAt).toLocaleDateString()}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditTagDialog(tag)}
                        >
                          <Pencil />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => handleDeleteTag(tag)}
                          disabled={deleteTag.isPending}
                        >
                          <Trash2 />
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Tag Create/Edit Dialog */}
      <Dialog open={tagDialog} onOpenChange={(o) => { if (!o) setTagDialog(false); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTag ? 'Edit Tag' : 'Create New Tag'}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 pt-2">
            <div className="space-y-1">
              <Label htmlFor="tag-name">Tag Name</Label>
              <Input
                id="tag-name"
                value={tagForm.tagName}
                onChange={(e) => setTagForm({ ...tagForm, tagName: e.target.value })}
                required
                placeholder="e.g., Supporters, Undecided"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="tag-description">Description (optional)</Label>
              <textarea
                id="tag-description"
                rows={2}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={tagForm.description}
                onChange={(e) => setTagForm({ ...tagForm, description: e.target.value })}
                placeholder="Describe when to use this tag"
              />
            </div>
            <div>
              <Label className="text-muted-foreground">Tag Color</Label>
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="color"
                  value={tagForm.color}
                  onChange={(e) => setTagForm({ ...tagForm, color: e.target.value })}
                  style={{ width: 50, height: 40, cursor: 'pointer', border: 'none' }}
                />
                <Input
                  value={tagForm.color}
                  onChange={(e) => setTagForm({ ...tagForm, color: e.target.value })}
                  className="w-[120px] font-mono"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTagDialog(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveTag}
              disabled={isSaving || !tagForm.tagName.trim()}
            >
              {isSaving ? 'Saving...' : editingTag ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TagsTab;
